import React, { useState } from 'react';
import { Box, Paper, Tabs, Tab, CircularProgress, Typography } from '@mui/material';
import {
  ShowChart as LineChartIcon,
  PieChart as PieChartIcon,
  TrendingUp as TrendingIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { MonthlyDynamicsRow, PharmacyDistRow, ProductDynamicsRow, UserActivityRow, JournalMovementRow } from '../types';
import { FilterOptions } from '../types';
import MonthlyDynamicsChart from './charts/MonthlyDynamicsChart';
import PharmacyDistChart from './charts/PharmacyDistChart';
import ProductDynamicsChart from './charts/ProductDynamicsChart';
import UsersActivityTable from './charts/UsersActivityTable';

interface AnalyticsPanelProps {
  // ── Data ──
  monthlyData: MonthlyDynamicsRow[];
  expiryData?: MonthlyDynamicsRow[];
  pharmacyDistData: PharmacyDistRow[];
  productDynData: ProductDynamicsRow[];
  movementsData?: JournalMovementRow[];
  usersActivity: UserActivityRow[];

  // ── Loading ──
  analyticsLoading: boolean;
  usersActivityLoading: boolean;

  // ── Filters / Options ──
  filterOptions: FilterOptions;
  selectedMonth: string;
  monthsList: { value: string; label: string }[];

  // ── Activity ──
  activitySearch: string;
  setActivitySearch: (v: string) => void;

  // ── Chart 3 local filters ──
  c3Pharmacy: string;
  setC3Pharmacy: (v: string) => void;
  c3Product: string | null;
  setC3Product: (v: string | null) => void;

  // ── Callbacks ──
  onFetchProductDynamics: (params: { pharmacyNum?: string; materialName?: string }) => void;
  onFetchUsersActivity: (month: string) => void;

  // ── Tab state (controlled from parent для синхронизации с useEffect) ──
  chartTab: number;
  setChartTab: (v: number) => void;
}

/**
 * Оркестратор вкладок аналитики журнала.
 * Содержит 4 подвкладки: Динамика сумм | Распределение | По товару | Активность пользователей.
 * Каждая вкладка рендерится только по необходимости.
 */
const AnalyticsPanel: React.FC<AnalyticsPanelProps> = ({
  monthlyData, expiryData = [], pharmacyDistData, productDynData, movementsData = [], usersActivity,
  analyticsLoading, usersActivityLoading,
  filterOptions, selectedMonth, monthsList,
  activitySearch, setActivitySearch,
  c3Pharmacy, setC3Pharmacy, c3Product, setC3Product,
  onFetchProductDynamics, onFetchUsersActivity,
  chartTab, setChartTab,
}) => {
  return (
    <Paper elevation={0} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {/* Подвкладки */}
      <Box sx={{ px: 2, pt: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={chartTab}
          onChange={(_, v) => setChartTab(v)}
          textColor="primary" indicatorColor="primary"
          sx={{ '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', fontSize: '0.85rem', minHeight: 42 } }}
        >
          <Tab icon={<LineChartIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Динамика по суммам" />
          <Tab icon={<PieChartIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Распределение по аптекам" />
          <Tab icon={<TrendingIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Динамика по товару" />
          <Tab icon={<PeopleIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Активность пользователей" />
        </Tabs>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* Глобальный спиннер аналитики (Графики 1 и 2) */}
        {analyticsLoading && chartTab < 2 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 2 }}>
            <CircularProgress size={40} />
            <Typography color="text.secondary">Загрузка аналитики…</Typography>
          </Box>
        ) : (
          <>
            {chartTab === 0 && <MonthlyDynamicsChart data={monthlyData} expiryData={expiryData} />}
            {chartTab === 1 && <PharmacyDistChart data={pharmacyDistData} />}
            {chartTab === 2 && (
              <ProductDynamicsChart
                data={productDynData}
                movements={movementsData}
                pharmacies={filterOptions.pharmacies}
                products={filterOptions.products}
                c3Pharmacy={c3Pharmacy}
                setC3Pharmacy={v => { setC3Pharmacy(v); onFetchProductDynamics({ pharmacyNum: v, materialName: c3Product ?? undefined }); }}
                c3Product={c3Product}
                setC3Product={v => { setC3Product(v); onFetchProductDynamics({ pharmacyNum: c3Pharmacy, materialName: v ?? undefined }); }}
              />
            )}
            {chartTab === 3 && (
              <UsersActivityTable
                usersActivity={usersActivity}
                usersActivityLoading={usersActivityLoading}
                activitySearch={activitySearch}
                setActivitySearch={setActivitySearch}
                selectedMonth={selectedMonth}
                monthsList={monthsList}
                onRefresh={() => onFetchUsersActivity(selectedMonth)}
              />
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

export default AnalyticsPanel;
